/* eslint-disable */
// @ts-nocheck
/*
Campaign Visualization Page with URL State Management

Features:
- Search parameters are automatically saved to URL
- Form is auto-populated when page loads with URL parameters
- Browser back/forward navigation preserves search state
- Auto-search when valid URL parameters are present
- Shareable URLs with search criteria
*/
import React, { Component } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { hasJustSuccess } from 'utils/request-state-utils';
import { Segment, Button, Container, Header, Message, Icon, Grid, Checkbox, Popup } from 'semantic-grab-ui/src';
import ProgressiveContainer from 'components/container/ProgressiveContainer';
import CampaignsVisualiser from 'omega-rtc/components/custom/CampaignsVisualiser';
import TridentCampaignQuickFilters from 'omega-rtc/components/custom/TridentCampaignQuickFilters';
import TridentCampaignAdvancedFilters from 'omega-rtc/components/custom/TridentCampaignAdvancedFilters';
import { hasTridentPermission } from 'omega-rtc/utils/user-utils';
import { visualiseCampaigns } from 'omega-rtc/utils/tree-utils';
import { getUniqueRuleTypes } from 'omega-rtc/utils/rule-utils';
import { defaultFilterTypes, statusList, targetTypeList } from 'data/trident-campaigns';
import { getFilterTypes, getListRuleTypes, searchCampaignsFull } from 'actions/newTridentExtra';

const maxCampaignsToVisualize = 1000;

export class TridentCampaignVisualisationView extends Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = {
      filterTypes: defaultFilterTypes,
      eventType: '',
      // Compulsory filter values
      compulsoryFilters: {
        country: '',
        targetUserType: '',
        eventType: '',
        status: '',
      },
      // Advanced filters
      advancedFilters: [],
      // Visualisation options
      visualiseOptions: {
        mergeConditionsByLHS: false,
      },
    };
  }

  componentDidMount() {
    if (_.get(window.Grab.config, 'CAMPAIGNS_FILTER_FEATURE_ENABLED', true)) {
      this.props.getFilterTypes();
    }
  }

  componentWillReceiveProps(nextProps: any) {
    if (hasJustSuccess(this.props.getFilterTypesRequest, nextProps.getFilterTypesRequest)) {
      this.setState({
        filterTypes: nextProps.filterTypes,
      });
    }

    // Handle search results
    if (hasJustSuccess(this.props.getSearchRequest, nextProps.getSearchRequest)) {
      // Fetch rule types when we have search results
      if (nextProps.searchResults && nextProps.searchResults.length > 0) {
        const firstCampaign = nextProps.searchResults[0];
        const owner = _.get(firstCampaign, 'owner', 'TARGETING');
        const targetUserType = _.get(firstCampaign, 'targetUserType', 'passenger');
        this.props.getListRuleTypes({ owner, targetUserType });
      }
    }

    // URL parameter syncing removed
  }

  // Get country options
  getCountryOptions = () => {
    return this.props.countries ? this.props.countries.map((country: any) => ({
      label: country.name,
      value: String(country.countryID),
    })) : [];
  };

  // Get status options
  getStatusOptions = () => {
    return statusList.map(status => ({
      label: status.text,
      value: status.value,
    }));
  };

  // Get target user type options
  getTargetUserTypeOptions = () => {
    return targetTypeList.map(userType => ({
      label: userType.text,
      value: userType.value,
    }));
  };

  // Get event type options from filterTypes
  getEventTypeOptions = () => {
    const eventTypeFilter = this.state.filterTypes['eventType'];
    if (!eventTypeFilter || !eventTypeFilter.options) {
      return [];
    }

    return eventTypeFilter.options.map((option: any) => ({
      label: option.text || option.label,
      value: option.value,
    }));
  };

  // Get advanced filter types (excluding compulsory filter types)
  getAdvancedFilterTypes = () => {
    const { filterTypes } = this.state;
    const compulsoryFilterTypes = ['countryID', 'status', 'targetUserType', 'eventType'];

    // Filter out compulsory filter types from advanced filters
    const advancedFilterTypes = Object.keys(filterTypes).reduce((acc: any, key: string) => {
      if (!compulsoryFilterTypes.includes(key)) {
        acc[key] = filterTypes[key];
      }
      return acc;
    }, {} as any);

    return advancedFilterTypes;
  };

  // Check if we should show the date range filter message
  shouldShowDateRangeMessage = () => {
    const { advancedFilters } = this.state;

    // Check if there are already startTime or endTime filters
    const hasStartTimeFilters = advancedFilters.some((filter: any) =>
      filter.lhs === 'startTime'
    );
    const hasEndTimeFilters = advancedFilters.some((filter: any) =>
      filter.lhs === 'endTime'
    );

    // Only show message if no time filters exist
    return !hasStartTimeFilters || !hasEndTimeFilters;
  };

  // Generate visualized node tree from campaigns
  getVisualisedNodeTree = (campaigns: any[], visualiseOptions: any) => {
    const eventType = this.state.eventType;

    try {
      const visualNode = visualiseCampaigns(campaigns, eventType, {
        mergeConditionsByLHS: visualiseOptions.mergeConditionsByLHS,
      });
      return visualNode;
    } catch (error) {
      // Fallback to empty tree structure
      return {
        type: 'event',
        payload: { eventType: eventType },
        linkedCampaigns: [],
        children: []
      };
    }
  };

  // Handle compulsory filters change
  handleCompulsoryFiltersChange = (field: string, value: any) => {
    this.setState((prevState: any) => ({
      compulsoryFilters: {
        ...prevState.compulsoryFilters,
        [field]: value,
      },
    }));
  };

  // Handle advanced filters change
  handleAdvancedFiltersChange = (filters: any[]) => {
    this.setState({
      advancedFilters: filters,
    });
  };

  // Handle visualise options change
  handleVisualiseOptionsChange = (option: string, value: boolean) => {
    this.setState((prevState: any) => ({
      visualiseOptions: {
        ...prevState.visualiseOptions,
        [option]: value,
      },
    }));
  };

  // Handle manual search button click
  handleSearch = () => {
    const { compulsoryFilters, advancedFilters } = this.state;
    const { country, targetUserType, eventType, status } = compulsoryFilters;

    // Validation: Check required compulsory fields
    const validationErrors: string[] = [];
    if (!country) validationErrors.push('Country must be selected');
    if (!targetUserType) validationErrors.push('Target User Type must be selected');
    if (!eventType) validationErrors.push('Event Type must be selected');
    if (!status) validationErrors.push('Status must be selected');

    // Validation: Check required time filters in advanced filters
    const hasStartTime = advancedFilters.some((filter: any) => filter.lhs === 'startTime');
    const hasEndTime = advancedFilters.some((filter: any) => filter.lhs === 'endTime');

    if (!hasStartTime) validationErrors.push('Start Time filter must be added to Advanced Filters');
    if (!hasEndTime) validationErrors.push('End Time filter must be added to Advanced Filters');

    if (validationErrors.length > 0) {
      alert(`Please fill in all required fields:\n${validationErrors.join('\n')}`);
      return;
    }

    // Update eventType state when search is triggered (this will trigger visualization update)
    this.setState({
      eventType: eventType,
    });

    // Convert compulsory filters to search format, include advanced filters
    const allFilters = [
      { lhs: 'eventType', operator: 'eq', rhs: eventType },
      ...advancedFilters,
    ];

    // Store search parameters in legacy format for API
    const searchParamsForState = {
      filters: JSON.stringify(allFilters),
      country: country,
      status: status,
      userType: targetUserType,
    };

    // Perform the actual search with the correct format
    this.performSearchAPI(searchParamsForState);
  };

  // Perform the actual search API call
  performSearchAPI = (searchParamsFormatted: any) => {
    const { filters: filtersString, country, status, userType } = searchParamsFormatted;

    let conditions: any[] = [];

    // Add advanced filters if they exist
    if (filtersString) {
      try {
        const filters = JSON.parse(filtersString);
        if (filters && filters.length > 0) {
          conditions = [...filters];
        }
      } catch (error) {
        // noop
      }
    }

    // Add country condition
    const countryID = Number(country);
    if (!_.isNaN(countryID) && countryID > 0) {
      conditions.push({
        lhs: 'countryID',
        operator: 'eq',
        rhs: countryID,
      });
    }

    // Add status condition
    if (!_.isEmpty(status)) {
      conditions.push({
        lhs: 'status',
        operator: 'eq',
        rhs: status,
      });
    }

    // Add user type condition
    if (!_.isEmpty(userType)) {
      conditions.push({
        lhs: 'targetUserType',
        operator: 'eq',
        rhs: userType,
      });
    }

    // this is hardcoded for now, to rule out the campaigns with only schedules
    conditions.push({
      lhs: 'nodeType',
      operator: 'eq',
      rhs: 'scenarios',
    });

    // Call the search API
    this.props.searchCampaignsFull('tridentCampaign', {
      conditions,
      page: 1,
      pageSize: maxCampaignsToVisualize, // Get more results for visualization
      returnOriginalNodes: true,
    });
  };

  render() {
    const {
      userPermissions,
      location,
      searchResults,
      getSearchRequest
    } = this.props as any;

    const canViewCampaign = hasTridentPermission(userPermissions, '', '', 'read');

    if (!_.isEmpty(userPermissions) && !canViewCampaign) {
      return (
          <Container>
            <Message negative>
              <Message.Header>Access Denied</Message.Header>
              <p>You do not have permission to view campaign visualisations.</p>
            </Message>
          </Container>
      );
    }

    // Use search results if available, otherwise use mock data
    const campaignsToVisualize = searchResults && searchResults.length > 0 ? searchResults : [];
    const nodeTree = campaignsToVisualize.length > 0 ? this.getVisualisedNodeTree(campaignsToVisualize, this.state.visualiseOptions) : null;
    const isSearching = getSearchRequest && getSearchRequest.isPending;
    const hasSearchResults = searchResults && searchResults.length > 0;

    return (
      <div>
        <Message info>
          <Icon name="info circle" />
          First time to scenarios visualiser? Refer to this <a href="https://grabtaxi.atlassian.net/wiki/spaces/MKT/pages/1005882487/How+to+use+Trident+global+scenarios+visualiser+WIP" target="__blank">Quick Guide</a>
        </Message>
        <Segment>
            <Header as="h3">
              <Icon name="filter" />
              Scenario filters:
            </Header>
            <ProgressiveContainer isLoading={isSearching}>
              <TridentCampaignQuickFilters
                fieldConfigs={{
                  country: {
                    name: 'Country',
                    type: 'singleSelect',
                    width: '200px',
                    options: this.getCountryOptions(),
                  },
                  targetUserType: {
                    name: 'Target User Type',
                    type: 'singleSelect',
                    width: '200px',
                    options: this.getTargetUserTypeOptions(),
                  },
                  status: {
                    name: 'Status',
                    type: 'singleSelect',
                    width: '200px',
                    options: this.getStatusOptions(),
                  },
                  eventType: {
                    name: 'Event Type',
                    type: 'singleSearch',
                    width: '250px',
                    searchPlaceholder: 'Search event types...',
                    options: this.getEventTypeOptions(),
                  },
                }}
                fieldValues={this.state.compulsoryFilters}
                onChange={this.handleCompulsoryFiltersChange}
              />


            {/* Advanced Filters Section */}
            <Grid style={{ marginTop: '20px' }}>
              {this.shouldShowDateRangeMessage() && (
                <Message warning style={{ marginLeft: '15px' }}>
                  <Icon name="warning circle" />
                  Please add filter on start time, end time, to limit the search to scenarios running in a certain date range :)
                </Message>
              )}
              <TridentCampaignAdvancedFilters
                filters={this.state.advancedFilters}
                filterTypes={this.getAdvancedFilterTypes()}
                onChange={this.handleAdvancedFiltersChange}
              />
            </Grid>

            {/* Search Button */}
            <div style={{ textAlign: 'left', marginTop: '20px' }}>
              <Button
                color="green"
                size="small"
                icon
                labelPosition="left"
                onClick={this.handleSearch}
                loading={isSearching}
                disabled={isSearching}
              >
                <Icon name="search" />
                Search
              </Button>
            </div>
            </ProgressiveContainer>
          </Segment>

          <Segment>
            <Header as="h3">
              <Icon name="sitemap" />
              Scenarios visualisation
            </Header>

            {isSearching && (
              <Message info>
                <Message.Header>Searching Campaigns...</Message.Header>
                <p>Please wait while we search for campaigns matching your criteria.</p>
              </Message>
            )}

            {!isSearching && hasSearchResults && (
              <Message success>
                <Message.Header>Search Results Found</Message.Header>
                <p>Visualizing {campaignsToVisualize.length} campaign(s) from your search results for event type: <strong>{this.state.eventType}</strong></p>
              </Message>
            )}

            {!isSearching && !hasSearchResults && (
            <Message info>
                <Message.Header>No Search Results</Message.Header>
                <p>No campaigns found matching your criteria. Use the search form above to find campaigns for event type: <strong>{this.state.eventType}</strong></p>
            </Message>
            )}

            {nodeTree && (
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '4px' }}>
                <Header as="h4" style={{ marginBottom: '10px', color: '#495057' }}>
                  <Icon name="options" />
                  Display options
                </Header>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Checkbox
                      toggle
                      label="Collapse same-variable conditions"
                      checked={this.state.visualiseOptions.mergeConditionsByLHS}
                      onChange={(e, { checked }) => this.handleVisualiseOptionsChange('mergeConditionsByLHS', checked || false)}
                    />
                    <Popup
                      trigger={
                        <Icon 
                          name="info circle" 
                          size="small" 
                          style={{ 
                            color: '#666',
                            cursor: 'help',
                            marginLeft: '4px'
                          }} 
                        />
                      }
                      content="When enabled, conditions targeting the same variable (e.g., multiple conditions checking food item ID) will be grouped together in the visualization to reduce complexity and improve readability."
                      position="top center"
                      inverted
                      size="small"
                    />
                  </div>
                </div>
              </div>
            )}

            <ProgressiveContainer isLoading={isSearching}>
              {nodeTree ? (
                <CampaignsVisualiser
                  nodeTree={nodeTree}
                  ruleTypes={this.props.listRuleTypes && Object.keys(this.props.listRuleTypes).length > 0
                    ? getUniqueRuleTypes(this.props.listRuleTypes)
                    : []
                  }
                />
              ) : !isSearching && (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#666',
                  fontSize: '16px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon name="search" size="big" style={{ marginBottom: '20px', opacity: 0.5 }} />
                  <div>
                    <strong>No Campaigns to Visualize</strong>
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '14px' }}>
                    Please search for campaigns using the form above to see the visualization.
                  </div>
                </div>
              )}
            </ProgressiveContainer>
          </Segment>
      </div>
    );
  }
}

export function mapStateToProps(state: any, ownProps: any) {
  const userPermissions = state.app.user.userPermissions;
  const countries = state.countries.entities;
  const { routeParams, router, location } = ownProps;

  return {
    userPermissions,
    routeParams,
    router,
    location,
    countries,
    user: state.app.user,
    searchResults: _.get(state, 'newTridents.fullCampaignSearchResults', []),
    getSearchRequest: _.get(state.requests, 'searchCampaignsFullRequest', {}),
    getFilterTypesRequest: _.get(state.requests, 'getFilterTypesRequest', {}),
    filterTypes: _.get(state, 'newTridents.filterTypes', {}),
    listRuleTypes: _.get(state, 'newTridents.listRuleTypes', {}),
  };
}

export default connect(mapStateToProps, {
  getFilterTypes,
  searchCampaignsFull,
  getListRuleTypes,
})(TridentCampaignVisualisationView);


